import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, BRAND } from "../App";
import { toast } from "sonner";
import { Lock, Mail, ArrowLeft, Eye, EyeOff } from "lucide-react";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/admin/dashboard");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      let msg = "Invalid email or password. Please try again.";
      if (err.response?.data?.detail) {
        const d = err.response.data.detail;
        msg = typeof d === "string" ? d : (Array.isArray(d) ? d.map(e => e?.msg || "").join(" ") : msg);
      } else if (err.message?.includes("Network")) {
        msg = "Network error. Please check your connection.";
      }
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-8">
          <ArrowLeft size={18} /> Back to Home
        </Link>

        <div className="bg-white p-8">
          <div className="text-center mb-8">
            <img src={BRAND.logo} alt={BRAND.name} className="h-16 mx-auto mb-4" />
            <h1 className="text-2xl font-black uppercase">Admin Login</h1>
            <p className="text-black/60 text-sm mt-2">Sign in to manage your business</p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-bold mb-2">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full border border-black/20 p-3 pl-10 focus:border-[#E31E24] focus:outline-none" placeholder="admin@example.com" />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold mb-2">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full border border-black/20 p-3 pl-10 pr-10 focus:border-[#E31E24] focus:outline-none" placeholder="Enter password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40 hover:text-black">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-[#E31E24] text-white font-bold uppercase py-4 hover:bg-[#C01A1F] disabled:opacity-50">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
